const PATTERNS: readonly string[] = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];
const START_B = 104;
const START_C = 105;
const CODE_B = 100;
const CODE_C = 99;
const STOP = 106;

export interface Code128Encoding {
  codes: number[];
  widths: number[];
  totalModules: number;
}

interface Segment {
  set: 'B' | 'C';
  text: string;
}

const isDigit = (char: string) => char >= '0' && char <= '9';

function digitRunLength(value: string, from: number): number {
  let n = 0;
  while (from + n < value.length && isDigit(value.charAt(from + n))) n += 1;
  return n;
}

function segment(value: string): Segment[] {
  const segments: Segment[] = [];
  const push = (set: 'B' | 'C', text: string) => {
    const last = segments[segments.length - 1];
    if (last && last.set === set) last.text += text;
    else segments.push({ set, text });
  };
  let i = 0;
  while (i < value.length) {
    const run = digitRunLength(value, i);
    const touchesEdge = i === 0 || i + run === value.length;
    if (run >= 6 || (run >= 4 && touchesEdge)) {
      const even = run % 2 === 0 ? run : run - 1;
      push('C', value.slice(i, i + even));
      i += even;
    } else {
      push('B', value.charAt(i));
      i += 1;
    }
  }
  return segments;
}

export function encodeCode128(value: string): Code128Encoding {
  if (value.length === 0) throw new Error('Cannot encode an empty barcode value');
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code < 32 || code > 126) throw new Error(`Character not encodable in Code 128: ${JSON.stringify(char)}`);
  }
  const segments = segment(value);
  const first = segments[0];
  if (!first) throw new Error('Cannot encode an empty barcode value');
  const codes: number[] = [first.set === 'C' ? START_C : START_B];
  let current: 'B' | 'C' = first.set;
  for (const seg of segments) {
    if (seg.set !== current) {
      codes.push(seg.set === 'C' ? CODE_C : CODE_B);
      current = seg.set;
    }
    if (seg.set === 'C') {
      for (let i = 0; i < seg.text.length; i += 2) codes.push(Number.parseInt(seg.text.slice(i, i + 2), 10));
    } else {
      for (const char of seg.text) codes.push(char.charCodeAt(0) - 32);
    }
  }
  let checksum = codes[0] ?? 0;
  for (let i = 1; i < codes.length; i += 1) checksum += (codes[i] ?? 0) * i;
  codes.push(checksum % 103);
  codes.push(STOP);
  const widths: number[] = [];
  for (const code of codes) {
    const pattern = PATTERNS[code];
    if (!pattern) throw new Error(`Invalid Code 128 symbol value: ${code}`);
    for (const digit of pattern) widths.push(Number.parseInt(digit, 10));
  }
  return { codes, widths, totalModules: widths.reduce((sum, w) => sum + w, 0) };
}

export function code128Svg(value: string, heightPx = 40, modulePx = 2): string {
  const { widths } = encodeCode128(value);
  let x = 0;
  const bars: string[] = [];
  widths.forEach((width, index) => {
    const w = width * modulePx;
    if (index % 2 === 0) bars.push(`<rect x="${x}" y="0" width="${w}" height="${heightPx}" fill="#000"/>`);
    x += w;
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${x}" height="${heightPx}" viewBox="0 0 ${x} ${heightPx}" shape-rendering="crispEdges">${bars.join('')}</svg>`;
}
